import * as lodash from 'lodash';
import { pairwise } from 'rxjs';

import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { environment } from '@environments/environment';

import { AppMenus } from '@configs/app.menus';
import { MODULES } from '@configs/modules.enum';
import { ROLES } from '@configs/roles.const';
import { ROUTE_PATHS } from '@configs/route-paths.const';

import { Menu } from '@models/view/menu.model';

import {
  PermissionService,
  RouterState,
  RouterUtilsService,
  StoreService,
  UserStore,
} from '@cxc/core/services';
import { OperationsState } from '@services/state/operation-state/operation.state';

import { normalizeUpperCase } from '@cxc/core/utils';
import { storeSignalBoolean, storeSignalObject } from '@cxc/core/utils';
import { toggleableSignal } from '@cxc/core/utils';
import { isStaffSuperAdmin } from '@utils/staff-super-admin';

@Injectable({ providedIn: 'root' })
export class DashboardState {
  router = inject(Router);
  appState = inject(RouterState);
  userStore = inject(UserStore);
  storeService = inject(StoreService);
  operationsState = inject(OperationsState);
  routerUtils = inject(RouterUtilsService);
  permissionService = inject(PermissionService);

  // PAGE TITLE
  pageTitle = signal('');

  // MAIN DRAWER
  private mainDrawerStoreKey = 'Main-Drawer';
  public mainDrawer = storeSignalBoolean(this.mainDrawerStoreKey, true);

  private menusStoreKey = 'Main-Drawer-Menus';
  public menus = storeSignalObject<Menu>(this.menusStoreKey, {} as Menu, true);

  // SECONDARY DRAWER
  public secondaryDrawer = toggleableSignal(false);

  toggleSecondaryDrawer() {
    this.secondaryDrawer.set(!this.secondaryDrawer());
  }

  setSecondaryDrawerToFalse() {
    this.secondaryDrawer.set(false);
  }
  constructor() {
    toObservable(this.userStore.signal)
      .pipe(takeUntilDestroyed())
      .subscribe(user => {
        if (!user.modules) {
          this.menus.set({} as Menu);
          return;
        }

        const _menus = lodash.cloneDeep(AppMenus);

        // LEN-2764: Admin's back offices answer only the SUPER_ADMIN of CXC
        if (!isStaffSuperAdmin(user)) {
          delete (_menus as Partial<typeof _menus>)[MODULES.SHELL_ADMIN];
        }

        // Self Service Tool only embeds DocFlow: without a URL for this
        // environment there is nothing to open, so hide it from every role
        if (!environment.DOCFLOW_URL) {
          _menus[MODULES.AIVA].subitems = _menus[MODULES.AIVA].subitems.filter(
            subitem => subitem.path !== ROUTE_PATHS.SELF_SERVICE_TOOL
          );
        }

        // Remove items from menu if user does not have
        // the permissions for that module
        if (user.role !== ROLES.SUPER_ADMIN) {
          lodash.keys(_menus).forEach(_key => {
            const key = _key as MODULES;
            const module = user.modules.find(
              module => module.type.toUpperCase() === normalizeUpperCase(key)
            );

            // Does not have the module
            if (!module) {
              delete _menus[key];
              return;
            }

            // Finally the permission check for top level
            if (
              _menus[key]!.requiredPermissions &&
              !this.permissionService.hasArrayOfPermissions(
                _menus[key]!.requiredPermissions!
              )
            ) {
              delete _menus[key];
              return;
            }

            // Has module, does it have all the submodules?
            // Submodules are managed with a permission string

            // No submodules declared
            if (_menus[key]!.subitems.length === 0) {
              return;
            }

            // Has submodules
            _menus[key]!.subitems = _menus[key]!.subitems.filter(submodule => {
              // This sumodule has no permission string declared,
              // we assume it is enabled
              if (
                !submodule.requiredPermissions ||
                submodule.requiredPermissions.length === 0
              ) {
                return true;
              }

              // Finally the permission check
              return this.permissionService.hasArrayOfPermissions(
                submodule.requiredPermissions
              );
            });
          });
        }

        this.menus.set(_menus);
      });

    // ===================== //
    // ==== PAGE TITLE ===== //
    // ===================== //

    toObservable(this.appState.url)
      .pipe(takeUntilDestroyed())
      .subscribe(url => {
        // Operation is active, show operation alias
        if (
          this.operationsState.isActive() &&
          this.operationsState.activeOperation()
        ) {
          this.pageTitle.set(this.operationsState.activeOperation()!.alias);
          return;
        }

        // Account is active
        if (url.startsWith('/account')) {
          this.pageTitle.set('Account');
          return;
        }

        this.pageTitle.set('');
        return;
      });

    toObservable(this.operationsState.activeOperation)
      .pipe(takeUntilDestroyed())
      .subscribe(activeOperation => {
        // Operation is active, show operation alias
        if (activeOperation) {
          this.pageTitle.set(activeOperation.alias);
        }
      });

    // ===================== //
    // ==== MAIN DRAWER ==== //
    // ===================== //

    // Listen to changes in drawer
    toObservable(this.mainDrawer)
      .pipe(takeUntilDestroyed())
      .subscribe(drawer => {
        if (drawer) {
          // Opens menu of active route
          this.menus.toggle(`${this.appState.urlFragments()[1]}.open`);

          return;
        }

        // Collapse all menus
        lodash.keys(this.menus()).forEach(key => {
          this.menus.setByKeyPath(`${key}.open`, false);
        });
      });

    // ====================== //
    // == SECONDARY DRAWER == //
    // ====================== //

    toObservable(this.appState.urlFragments)
      .pipe(pairwise(), takeUntilDestroyed())
      .subscribe(([prevFragments, fragments]) => {
        let activeMenuSubitemPath = '';

        if (fragments.length >= 3) {
          activeMenuSubitemPath = `/${fragments[0]}/${fragments[1]}/${fragments[2]}`;
        } else {
          activeMenuSubitemPath = '/' + lodash.join(fragments, '/');
        }

        const routeDataOfActiveMenu = this.routerUtils.getIterableRouteOfUrl(
          activeMenuSubitemPath
        )?.data;
        const drawer = routeDataOfActiveMenu
          ? routeDataOfActiveMenu['drawer']
          : false;
        const wasPreviousRouteFacility = prevFragments.includes('facility');

        if (drawer) {
          if (!wasPreviousRouteFacility) this.secondaryDrawer.set(true);
          this.mainDrawer.set(false);
          return;
        }

        this.secondaryDrawer.set(false);
        this.mainDrawer.set(true);
      });
  }
}
